import {
  Flex,
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import {
  AwardLetterDto,
  AwardLetterEvaluationDto,
  AwardLetterScoreDto,
  AwardLetterPatchDto,
  AwardLetterStatusDto,
  AwardLetterTypeDto,
  useApproveAwardLetterMutation,
  useReopenAwardLetterMutation,
  useUpdateAwardLetterMutation, AiFeature } from "../../../autogen/bff-api";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";
import { visibleLength } from "../../../common/text/visibleLength";
import { ListLoadError } from "../../../common/ListLoadError";
import { useSub } from "../../../common/subscription/useSub";
import {
  forRecipient,
  letterIntro,
  clausesAsShown,
  StatutoryClauseContext,
  warnsAgainstNamingWinner,
} from "./awardLetterTemplates";
import { AwardLetterPreview } from "./AwardLetterPreview";
import {
  AwardLetterTextCounter,
  AwardLetterTextLimitMessage,
  exceedsAwardLetterTextLimit,
  MAX_AWARD_LETTER_TEXT_LENGTH,
} from "./AwardLetterTextLimit";
import { AwardLetterDraft, AwardLetterDraftInput, useAwardLetterDraft } from "./useAwardLetterDraft";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";
import { comparedStageLabel } from "./comparedStage";
import { relativeAdvantagesLabel } from "./relativeAdvantagesLabel";
import { AskFurtherButton } from "../../../common/agent/AskFurtherButton";

interface Props {
  eventId: string;
  letter: AwardLetterDto;
  evaluation?: AwardLetterEvaluationDto;
  criterionStandings?: Record<string, AwardLetterScoreDto>;
  aiInput: AwardLetterDraftInput;
  criteriaLoad: CriteriaLoad;
  winnerName: string;
  namesWinnerToOtherBidders: boolean;
  eventTitle: string;
  senderName?: string | null;
  signerName?: string | null;
  clauseContext: StatutoryClauseContext;
  relativeAdvantages?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface CriteriaLoad {
  isLoading: boolean;
  isError: boolean;
  isRetrying: boolean;
  retry: () => void;
}

const AUTOSAVE_DEBOUNCE_MS = 300;

export const AwardLetterEditor = (props: Props) => {
  const { eventId, letter } = props;
  const [update] = useUpdateAwardLetterMutation();
  const editorErrorConfig = useApiErrorEditorConfig();

  useCreateEditor<AwardLetterDto, AwardLetterPatchDto>(letter.id, {
    initialState: letter,
    save: (patch) => update({ eventId, letterId: letter.id, awardLetterPatchDto: patch }).unwrap(),
    ...editorErrorConfig,
  });

  return <AwardLetterEditorBody {...props} />;
};

const AwardLetterEditorBody = ({
  eventId,
  letter,
  evaluation,
  criterionStandings,
  aiInput,
  criteriaLoad,
  winnerName,
  namesWinnerToOtherBidders,
  eventTitle,
  senderName,
  signerName,
  clauseContext,
  relativeAdvantages,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const clauses = clausesAsShown(
    forRecipient(clauseContext, letter.type === AwardLetterTypeDto.Rejection ? winnerName : null),
    letter.sentClauses,
  );
  const terms = useProcurementTerms();
  const toast = useToast();
  const apiError = useApiError();
  const sub = useSub();
  const preview = useDisclosure();
  const [approve, { isLoading: isApproving }] = useApproveAwardLetterMutation();
  const [reopen, { isLoading: isReopening }] = useReopenAwardLetterMutation();
  const { state, updateField, entityStatus } = useEditor<AwardLetterDto, AwardLetterPatchDto>(letter.id);

  const isRejection = state.type === AwardLetterTypeDto.Rejection;
  const forRejectedTender = isRejection && !!state.forRejectedTender;
  const isApproved = state.status === AwardLetterStatusDto.Approved;
  const isSent = state.status === AwardLetterStatusDto.Sent;
  const isLocked = isApproved || isSent;
  const withholdsWinner = warnsAgainstNamingWinner(isRejection, namesWinnerToOtherBidders);
  const supplierName = state.supplierName || t("Unknown") || "";
  const intro = letterIntro({ eventTitle, winnerName, senderName, clauseContext, isRejection, forRejectedTender });
  const justifications = useMemo(
    () => state.content.criterionJustifications ?? {},
    [state.content.criterionJustifications],
  );
  const freeText = state.content.freeText ?? "";
  const explainsDeparture = isRejection && !!state.explainsRankingDeparture;
  const departureExplanation = state.content.rankingDepartureExplanation ?? "";
  const lacksDepartureExplanation = explainsDeparture && visibleLength(departureExplanation) === 0;
  const letterScoreFor = (criterionId: string) => criterionStandings?.[criterionId];
  const recipientScoreFor = (criterionId: string) => {
    const letterScore = letterScoreFor(criterionId);
    if (letterScore) return letterScore.supplierPoints ?? null;
    return aiInput.recipient.responses.find((r) => r.criterionId === criterionId)?.points ?? null;
  };
  const winnerScoreFor = (criterionId: string) => {
    const letterScore = letterScoreFor(criterionId);
    if (letterScore) return letterScore.winnerPoints ?? null;
    return aiInput.winner.responses.find((r) => r.criterionId === criterionId)?.points ?? null;
  };
  const winnerLeadsOn = (criterionId: string) => {
    const winner = letterScoreFor(criterionId)?.winnerPoints;
    const recipient = letterScoreFor(criterionId)?.supplierPoints;
    return winner == null || recipient == null || winner > recipient;
  };
  const chosenTenderAdvantages = forRejectedTender ? state.chosenTenderAdvantages?.trim() || null : null;
  const requiresJustifications = isRejection && !forRejectedTender && !clauseContext.eventRunsPrivateFlow;
  const criteriaUnavailable = requiresJustifications && (criteriaLoad.isLoading || criteriaLoad.isError);
  const unjustifiedCriteria = requiresJustifications
    ? aiInput.criteria.filter((c) => winnerLeadsOn(c.id) && visibleLength(justifications[c.id] ?? "") === 0)
    : [];
  const winnerLeadsSomewhere = Object.values(criterionStandings ?? {}).some(
    (score) => score.winnerPoints != null && score.supplierPoints != null && score.winnerPoints > score.supplierPoints,
  );
  const awardWroteNoAdvantages =
    forRejectedTender && !clauseContext.eventRunsPrivateFlow && !clauseContext.designContest && !chosenTenderAdvantages;
  const mustStateTheChoice =
    !explainsDeparture &&
    (awardWroteNoAdvantages ||
      (requiresJustifications &&
        aiInput.criteria.length > 0 &&
        !aiInput.criteria.some((c) => winnerLeadsOn(c.id)) &&
        !winnerLeadsSomewhere));
  const lettersJustifications = forRejectedTender ? {} : justifications;
  const statesNoReason =
    visibleLength(freeText) === 0 &&
    Object.values(lettersJustifications).every((text) => visibleLength(text ?? "") === 0);
  const lacksReasonForTheChoice = mustStateTheChoice && statesNoReason;
  const advantagesGoInFreeText = mustStateTheChoice && awardWroteNoAdvantages;
  const tooLongCriteria = aiInput.criteria.filter((c) => exceedsAwardLetterTextLimit(lettersJustifications[c.id]));
  const departureExplanationTooLong = explainsDeparture && exceedsAwardLetterTextLimit(departureExplanation);
  const freeTextTooLong = exceedsAwardLetterTextLimit(freeText);
  const departureExplanationLabel = t("Why the contract is not awarded to your tender");
  const freeTextLabel = advantagesGoInFreeText
    ? t("The winning tender's characteristics and relative advantages")
    : t("Additional text (optional)");
  const tooLongFields = [
    ...(departureExplanationTooLong ? [departureExplanationLabel] : []),
    ...tooLongCriteria.map((c) => c.name),
    ...(freeTextTooLong ? [freeTextLabel] : []),
  ];
  const approvalHeld =
    criteriaUnavailable ||
    unjustifiedCriteria.length > 0 ||
    tooLongFields.length > 0 ||
    lacksDepartureExplanation ||
    lacksReasonForTheChoice;
  const approvalHeldBecause = tooLongFields.length > 0
    ? t("Shorten the text to at most {{max}} characters before approving: {{fields}}", {
        max: MAX_AWARD_LETTER_TEXT_LENGTH,
        fields: tooLongFields.join(", "),
      })
    : lacksDepartureExplanation
    ? t("Explain to the supplier why the contract is not awarded to its tender before approving.")
    : lacksReasonForTheChoice && awardWroteNoAdvantages
    ? t("No relative advantages were written at the award. Describe the winning tender's characteristics and relative advantages before approving.")
    : lacksReasonForTheChoice
    ? t("The winner didn't score higher than this tender on any criterion. Say in the additional text why the winning tender was chosen before approving.")
    : criteriaLoad.isError
    ? t("The criteria could not be loaded, so the letter cannot be approved yet.")
    : criteriaLoad.isLoading
    ? t("Loading the criteria to justify …")
    : t("Justify every criterion before approving: {{criteria}}", {
        criteria: unjustifiedCriteria.map((c) => c.name).join(", "),
      });

  const comparedStage = isRejection ? comparedStageLabel(t, evaluation?.comparedRoundNumber) : null;
  const winnerLabel = comparedStage ? t("winner in {{stage}}", { stage: comparedStage }) : t("winner");

  // The BFF replaces criterionJustifications wholesale (Patch<Map>), so always send the full map.
  // The coordinator's optimistic state recurses into the map per key, which lines up with the
  // server's whole-map replace because we're sending the whole intended state.
  const setJustification = (criterionId: string, value: string) => {
    updateField(
      { content: { criterionJustifications: { ...justifications, [criterionId]: value } } },
      { debounceMs: AUTOSAVE_DEBOUNCE_MS },
    );
  };
  const setDepartureExplanation = (value: string) => {
    updateField({ content: { rankingDepartureExplanation: value } }, { debounceMs: AUTOSAVE_DEBOUNCE_MS });
  };
  const setFreeText = (value: string) => {
    updateField({ content: { freeText: value } }, { debounceMs: AUTOSAVE_DEBOUNCE_MS });
  };

  const onDraft = useCallback(
    (d: AwardLetterDraft) => {
      updateField({
        content: {
          criterionJustifications: { ...justifications, ...d.criterionJustifications },
          ...(d.freeText ? { freeText: d.freeText } : {}),
        },
      });
    },
    [justifications, updateField],
  );

  // Atomic save + approve: we send the latest content as the approve body, so the BFF applies
  // the content patch and the approval in one transaction. This eliminates any race between
  // a still-pending autosave and the approve call.
  const onApprove = async () => {
    try {
      await approve({
        eventId,
        letterId: letter.id,
        awardLetterPatchDto: {
          content: {
            criterionJustifications: lettersJustifications,
            freeText: freeText.trim() || null,
            ...(explainsDeparture ? { rankingDepartureExplanation: departureExplanation.trim() || null } : {}),
          },
        },
      }).unwrap();
      toast({ title: t("Letter approved"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const onReopen = async () => {
    try {
      await reopen({ eventId, letterId: letter.id }).unwrap();
      toast({ title: t("Letter re-opened for edit"), status: "info", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const letterTitle = forRejectedTender
    ? t("Award letter to a rejected tenderer")
    : isRejection
    ? t("Rejection letter")
    : clauseContext.designContest
      ? t("Letter to the winner")
      : t("Winner letter");
  const letterAsShown = () =>
    [
      intro,
      chosenTenderAdvantages,
      ...aiInput.criteria
        .filter((criterion) => visibleLength(lettersJustifications[criterion.id] ?? "") > 0)
        .map((criterion) => `${criterion.name}: ${lettersJustifications[criterion.id]?.trim()}`),
      freeText.trim(),
    ]
      .filter(Boolean)
      .join("\n\n");

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="smBackgroundSecondary">
          <ModalHeader>
            <HStack spacing="3" flexWrap="wrap">
              <Text>
                {letterTitle}: {supplierName}
              </Text>
              <Badge colorScheme={statusColorScheme(state.status, isRejection)}>
                {statusLabel(state.status, isRejection, t)}
              </Badge>
              <SaveIndicator status={entityStatus} />
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="5">
              <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p="3">
                <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="1">
                  {t("Template (intro)")}
                </Text>
                <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                  {intro}
                </Text>
              </Box>

              {chosenTenderAdvantages && (
                <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p="3">
                  <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="1">
                    {relativeAdvantagesLabel(t, !!clauseContext.designContest)}
                  </Text>
                  <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                    {chosenTenderAdvantages}
                  </Text>
                  <Text fontSize="xs" color="smMuted" mt="2">
                    {t("From the award decision. It is always part of this letter.")}
                  </Text>
                </Box>
              )}

              {!isLocked && !forRejectedTender && sub.hasAiAccess && sub.allowsAiFeature(AiFeature.AwardLetterDraft) && (
                <AiDraftSection
                  eventId={eventId}
                  disabled={isApproving || isReopening}
                  input={aiInput}
                  onDraft={onDraft}
                  justifications={justifications}
                  freeText={freeText}
                  label={
                    isRejection
                      ? t("Suggest justifications and closing text") || ""
                      : t("Suggest a congratulatory note") || ""
                  }
                />
              )}
              {!isLocked && sub.hasAiAccess && !statesNoReason && (
                <Box>
                  <AskFurtherButton
                    result={() => ({
                      source: isRejection ? t("Suggested justifications") : t("Suggested congratulatory note"),
                      subject: `${letterTitle}: ${supplierName}`,
                      text: letterAsShown(),
                    })}
                    beforeOpen={onClose}
                  />
                </Box>
              )}

              {withholdsWinner && !isLocked && (
                <Text fontSize="sm" color="smWarning">
                  {clauseContext.designContest
                    ? t("The other participants in this contest are not told who won. Do not name the winner in the text you write.")
                    : t("The other suppliers in this competition are not told who won. Do not name the winner in the text you write.")}
                </Text>
              )}

              {explainsDeparture && (
                <FormControl isRequired isInvalid={!isLocked && (lacksDepartureExplanation || departureExplanationTooLong)}>
                  <FormLabel fontSize="sm">{departureExplanationLabel}</FormLabel>
                  <Textarea
                    value={departureExplanation}
                    onChange={(e) => setDepartureExplanation(e.target.value)}
                    isDisabled={isLocked}
                    bg="smBackground"
                    rows={4}
                  />
                  {!isLocked && (
                    <>
                      <FormHelperText>
                        {t(
                          "This supplier was ranked above the winner, and reads this text. It starts from the reason you gave at award, which was written for the protocol, so rewrite it for the supplier.",
                        )}
                      </FormHelperText>
                      <AwardLetterTextCounter text={departureExplanation} />
                    </>
                  )}
                  <FormErrorMessage>
                    {departureExplanationTooLong ? (
                      <AwardLetterTextLimitMessage />
                    ) : (
                      t("Explain to the supplier why the contract is not awarded to its tender.")
                    )}
                  </FormErrorMessage>
                </FormControl>
              )}

              {requiresJustifications && (criteriaLoad.isLoading || criteriaLoad.isError || aiInput.criteria.length > 0) && (
                <Box>
                  <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="2">
                    {t("Justification per criterion")}
                  </Text>
                  {criteriaLoad.isLoading && <Spinner size="sm" color="smMuted" />}
                  {criteriaLoad.isError && (
                    <ListLoadError
                      message={t("Could not load the award criteria.")}
                      onRetry={criteriaLoad.retry}
                      isRetrying={criteriaLoad.isRetrying}
                    />
                  )}
                  <Stack spacing="3">
                    {aiInput.criteria.map((c) => (
                      <FormControl
                        key={c.id}
                        isInvalid={!isLocked && (unjustifiedCriteria.includes(c) || tooLongCriteria.includes(c))}
                      >
                        <FormLabel fontSize="sm" mb="1">
                          {c.name} ({c.weightPercent}%) ·{" "}
                          <Text as="span" color="smMuted">
                            {t("Your score")}: {recipientScoreFor(c.id) ?? "–"} / {winnerLabel}:{" "}
                            {winnerScoreFor(c.id) ?? "–"}
                          </Text>
                        </FormLabel>
                        <Textarea
                          value={justifications[c.id] ?? ""}
                          onChange={(e) => setJustification(c.id, e.target.value)}
                          isDisabled={isLocked}
                          bg="smBackground"
                          rows={3}
                          placeholder={
                            (winnerLeadsOn(c.id)
                              ? t("Why the winner scored better on this criterion…")
                              : t("A comment on this criterion (optional)…")) ?? ""
                          }
                        />
                        {!isLocked && <AwardLetterTextCounter text={justifications[c.id]} />}
                        <FormErrorMessage>
                          {tooLongCriteria.includes(c) ? (
                            <AwardLetterTextLimitMessage />
                          ) : (
                            t("Explain why the winner scored better on this criterion.")
                          )}
                        </FormErrorMessage>
                      </FormControl>
                    ))}
                  </Stack>
                </Box>
              )}

              <FormControl
                isRequired={advantagesGoInFreeText}
                isInvalid={!isLocked && (freeTextTooLong || (advantagesGoInFreeText && lacksReasonForTheChoice))}
              >
                <FormLabel fontSize="sm">{freeTextLabel}</FormLabel>
                <Textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  isDisabled={isLocked}
                  bg="smBackground"
                  rows={3}
                />
                {!isLocked && <AwardLetterTextCounter text={freeText} />}
                {isRejection && !forRejectedTender && !isLocked && !!relativeAdvantages?.trim() && (
                  <Button
                    mt="2"
                    size="sm"
                    variant="outline"
                    colorScheme="teal"
                    title={t("Insert the winning tender's relative advantages from the award") ?? undefined}
                    isDisabled={freeText.includes(relativeAdvantages)}
                    onClick={() => setFreeText(freeText.trim() ? `${freeText.trim()}\n\n${relativeAdvantages}` : relativeAdvantages)}
                  >
                    {t("Insert relative advantages from the award")}
                  </Button>
                )}
                {isRejection && !forRejectedTender && !isLocked && !relativeAdvantages?.trim() && (
                  <Text mt="2" fontSize="sm" color="smMuted">
                    {t("No relative advantages were written at the award, so there is none to insert here.")}
                  </Text>
                )}
                <FormErrorMessage>
                  {freeTextTooLong ? (
                    <AwardLetterTextLimitMessage />
                  ) : (
                    t("Describe the winning tender's characteristics and relative advantages.")
                  )}
                </FormErrorMessage>
              </FormControl>

              {(clauses.standstill || clauses.complaint) && (
                <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p="3">
                  <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="1">
                    {clauseContext.designContest ? t("Template") : t("Template (standstill and complaint)")}
                  </Text>
                  {clauses.standstill && (
                    <Text fontSize="sm" color="smMuted" whiteSpace="pre-wrap">
                      {clauses.standstill}
                    </Text>
                  )}
                  {clauses.complaint && (
                    <Text fontSize="sm" color="smMuted" whiteSpace="pre-wrap" mt="2">
                      {clauses.complaint}
                    </Text>
                  )}
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter flexDirection="column" alignItems="stretch" gap="2">
            {!isLocked && approvalHeld && (
              <Text fontSize="sm" color="smMuted" textAlign="right" data-testid="approval-held-reason">
                {approvalHeldBecause}
              </Text>
            )}
            <Flex gap="2" justify="flex-end" wrap="wrap">
              <Button variant="ghost" flexShrink={0} onClick={preview.onOpen}>
                {t("Preview")}
              </Button>
              {isSent && (
                <Button flexShrink={0} onClick={onClose}>
                  {t("Close")}
                </Button>
              )}
              {isApproved && (
                <Button variant="outline" flexShrink={0} onClick={onReopen} isLoading={isReopening}>
                  {t("Re-open for edit")}
                </Button>
              )}
              {!isLocked && (
                <Button
                  colorScheme="teal"
                  flexShrink={0}
                  onClick={onApprove}
                  isLoading={isApproving}
                  isDisabled={approvalHeld}
                >
                  {t("Mark as approved")}
                </Button>
              )}
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={preview.isOpen} onClose={preview.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {clauseContext.designContest ? t("Preview of the letter") : t("Preview of award letter")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AwardLetterPreview
              type={state.type}
              supplierName={supplierName}
              winnerName={winnerName}
              eventTitle={eventTitle}
              senderName={senderName}
              signerName={signerName}
              criterionJustifications={forRejectedTender && !isLocked ? {} : justifications}
              chosenTenderAdvantages={chosenTenderAdvantages}
              freeText={freeText}
              rankingDepartureExplanation={explainsDeparture ? departureExplanation : null}
              criteria={aiInput.criteria}
              evaluation={evaluation}
              sentAt={state.sentAt}
              clauseContext={clauseContext}
              sentClauses={letter.sentClauses}
              forRejectedTender={forRejectedTender}
              scoreHeading={terms.scorePerAwardCriterion}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const statusColorScheme = (status: AwardLetterStatusDto, isRejection: boolean): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return "green";
    case AwardLetterStatusDto.Approved:
      return "teal";
    default:
      return isRejection ? "gray" : "teal";
  }
};

const statusLabel = (
  status: AwardLetterStatusDto,
  isRejection: boolean,
  t: (k: string) => string,
): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return t("Sent");
    case AwardLetterStatusDto.Approved:
      return t("Approved");
    default:
      return isRejection ? t("Not selected") : t("Winner");
  }
};

const SaveIndicator = ({ status }: { status: "idle" | "pending" | "saving" | "saved" | "error" }) => {
  const { t } = useTranslation();
  if (status === "saving" || status === "pending") {
    return <Text fontSize="xs" color="smMuted">{t("Saving…")}</Text>;
  }
  if (status === "saved") {
    return <Text fontSize="xs" color="smMuted">{t("Saved")}</Text>;
  }
  if (status === "error") {
    return <Text fontSize="xs" color="smError">{t("Save failed")}</Text>;
  }
  return null;
};

const AiDraftSection = ({
  eventId,
  input,
  onDraft,
  disabled,
  label,
  justifications,
  freeText,
}: {
  eventId: string;
  input: AwardLetterDraftInput;
  onDraft: (d: AwardLetterDraft) => void;
  disabled?: boolean;
  label: string;
  justifications: Record<string, string>;
  freeText: string;
}) => {
  return (
    <AiDraftButton
      eventId={eventId}
      input={input}
      onDraft={onDraft}
      disabled={disabled}
      label={label}
      justifications={justifications}
      freeText={freeText}
    />
  );
};

const AiDraftButton = ({
  eventId,
  input,
  onDraft,
  disabled,
  label,
  justifications,
  freeText,
}: {
  eventId: string;
  input: AwardLetterDraftInput;
  onDraft: (d: AwardLetterDraft) => void;
  disabled?: boolean;
  label: string;
  justifications: Record<string, string>;
  freeText: string;
}) => {
  const { t } = useTranslation();
  const { draft, isLoading, hasError, failedCriterionIds } = useAwardLetterDraft(eventId);

  // Re-clicking only redrafts criteria that are still empty, so a flaky criterion can be retried
  // without re-rolling the ones that already succeeded. When nothing is empty, redraft everything.
  const onClick = async () => {
    const empty = input.criteria.filter((c) => visibleLength(justifications[c.id] ?? "") === 0);
    const criteria = empty.length > 0 ? empty : input.criteria;
    const result = await draft({ ...input, criteria }, { includeClosing: !freeText.trim() });
    if (result) onDraft(result);
  };

  return (
    <Box>
      <Button
        size="sm"
        leftIcon={<FaMagic />}
        variant="outline"
        colorScheme="purple"
        onClick={onClick}
        isLoading={isLoading}
        isDisabled={disabled}
      >
        {label}
      </Button>
      {hasError && (
        <Text fontSize="sm" color="smError" mt="1">
          {t("Could not generate a suggestion. Please try again.")}
        </Text>
      )}
      {!hasError && failedCriterionIds.length > 0 && (
        <Text fontSize="sm" color="smWarning" mt="1">
          {t("Could not generate {{failed}} of {{total}} justifications. Click again to retry the rest.", {
            failed: failedCriterionIds.length,
            total: input.criteria.length,
          })}
        </Text>
      )}
    </Box>
  );
};
